import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  CardHeader,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  FormControlLabel,
  Collapse,
  IconButton,
  Tooltip,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  Payment,
  Person,
  Home,
  CreditCard,
  AddShoppingCart,
  CheckCircle,
} from "@mui/icons-material";
import { useSpring, animated } from "@react-spring/web";

const defaultPlan = {
  id: "default",
  pacientes: 100,
  precio: 149.0,
  titulo:
    "Tu plan incluye capacidad para crear y atender hasta 250 pacientes simultaneos",
};

const plans = [
  { id: 1, pacientes: 50, precio: 79, titulo: "50 pacientes extra" },
  { id: 2, pacientes: 100, precio: 399, titulo: "100 pacientes extra" },
  { id: 3, pacientes: 500, precio: 699, titulo: "500 pacientes extra" },
];

const CheckoutContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  gap: theme.spacing(4),
  padding: theme.spacing(4),
  background: "linear-gradient(to bottom right, #f4f7fb, #ffffff)",
  borderRadius: theme.shape.borderRadius,
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
  },
}));

const FormContainer = styled(Box)(({ theme }) => ({
  flex: 2,
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
}));

const SummaryContainer = styled(Paper)(({ theme }) => ({
  flex: 1,
  padding: theme.spacing(3),
  backgroundColor: "#ffffffe0",
  backdropFilter: "blur(10px)",
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.spacing(2),
  boxShadow: theme.shadows[4],
  position: "sticky",
  top: theme.spacing(4),
  alignSelf: "flex-start",
  zIndex: 1000,
}));

const AnimatedCard = styled(animated(Card))(({ theme }) => ({
  cursor: "pointer",
  transition: "transform 0.2s ease, box-shadow 0.2s ease",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  height: "100%",
  borderRadius: theme.spacing(2),
  position: "relative",
  "&:hover": {
    transform: "translateY(-5px)",
    boxShadow: theme.shadows[6],
  },
}));

const SectionBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.spacing(2),
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: "#ffffffcc",
  backdropFilter: "blur(8px)",
  boxShadow: theme.shadows[1],
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: theme.spacing(1.5),
    backgroundColor: "#fff",
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.spacing(1.5),
  textTransform: "none",
  fontWeight: "bold",
}));

const HeaderText = styled(Typography)(({ theme }) => ({
  fontWeight: "bold",
  fontSize: "1.25rem",
}));

const SubtitleIconBox = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  marginBottom: theme.spacing(2),
  gap: theme.spacing(1),
}));

const Checkout = () => {
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState("card");
  const [formData, setFormData] = useState({
    email: "",
    firstName: "",
    lastName: "",
    company: "",
    address: "",
    apt: "",
    city: "",
    country: "México",
    state: "",
    zip: "",
    phone: "",
    cardNumber: "",
    cardName: "",
    cardExp: "",
    cardCvc: "",
  });

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSelectPlan = (plan) => {
    if (plan.id !== "default") {
      if (selectedPlan && selectedPlan.id === plan.id) {
        setSelectedPlan(null);
      } else {
        setSelectedPlan(plan);
      }
    }
  };

  const handleSubmit = () => {
    const totalWithoutIVA =
      defaultPlan.precio + (selectedPlan ? selectedPlan.precio : 0);
    const IVA = totalWithoutIVA * 0.08;
    const total = totalWithoutIVA + IVA;
    console.log("Procesando pago...", formData, selectedPlan, total);
  };

  const totalWithoutIVA =
    defaultPlan.precio + (selectedPlan ? selectedPlan.precio : 0);
  const IVA = totalWithoutIVA * 0.08;
  const total = totalWithoutIVA + IVA;

  const springProps = useSpring({
    transform: paymentMethod === "card" ? "scale(1)" : "scale(0)",
    opacity: paymentMethod === "card" ? 1 : 0,
    config: { tension: 170, friction: 26 },
  });

  const selectedPlanPrice = selectedPlan ? selectedPlan.precio : 0;

  return (
    <CheckoutContainer>
      <FormContainer>
        <SectionBox>
          <Grid container spacing={2} alignItems="stretch">
            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  backgroundColor: "#ecf8f3",
                  borderRadius: "16px",
                  padding: 2,
                  border: "1px solid",
                  borderColor: "primary.main",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                  position: "relative",
                }}
              >
                <CheckCircle color="success" sx={{ mr: 1 }} />
                <Box>
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    color="primary.dark"
                  >
                    {`${defaultPlan.titulo} por $${defaultPlan.precio} pesos al mes.`}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Esto siempre va incluido en cada mensualidad.
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <HeaderText gutterBottom color="primary.dark">
                <AddShoppingCart
                  color="primary"
                  sx={{ mb: "-5px", mr: "5px" }}
                />
                Tambien puedes agregar a tu plan mas capacidad para crear
                pacientes.
              </HeaderText>
            </Grid>
            {plans.map((plan) => {
              const isSelected = selectedPlan && selectedPlan.id === plan.id;
              return (
                <Grid item xs={12} sm={6} md={4} key={plan.id}>
                  <AnimatedCard
                    variant="outlined"
                    onClick={() => handleSelectPlan(plan)}
                    style={{
                      borderColor: isSelected ? "primary.main" : "#ddd",
                      boxShadow: isSelected
                        ? "0 4px 20px rgba(0,0,0,0.15)"
                        : "none",
                      backgroundColor: isSelected ? "#e3f2fd" : "#ffffff",
                      borderRadius: "16px",
                    }}
                  >
                    <CardHeader
                      avatar={
                        isSelected ? (
                          <CheckCircle color="primary" />
                        ) : (
                          <Tooltip title="Agregar">
                            <IconButton>
                              <AddShoppingCart />
                            </IconButton>
                          </Tooltip>
                        )
                      }
                      title={plan.titulo}
                      titleTypographyProps={{
                        variant: "h6",
                        color: isSelected ? "primary.dark" : "text.primary",
                      }}
                    />
                    <CardContent>
                      <Typography
                        variant="body2"
                        sx={{ color: "text.secondary" }}
                      >
                        Costo: ${plan.precio} MXN
                      </Typography>
                    </CardContent>
                    <CardActions>
                      <StyledButton
                        variant={isSelected ? "contained" : "outlined"}
                        fullWidth
                        onClick={() => handleSelectPlan(plan)}
                        color={isSelected ? "primary" : "inherit"}
                      >
                        {isSelected ? "Seleccionado" : "Seleccionar"}
                      </StyledButton>
                    </CardActions>
                  </AnimatedCard>
                </Grid>
              );
            })}
            <Grid item xs={12}>
              <HeaderText gutterBottom color="primary.dark">
                O puedes comprarlos despues si lo necesitas en la seccion de "Mi
                cuenta"
              </HeaderText>
            </Grid>
          </Grid>
        </SectionBox>
        <SectionBox>
          <HeaderText gutterBottom color="primary.dark">
            Datos de contacto
          </HeaderText>
          <SubtitleIconBox>
            <Home color="primary" />
            <Typography variant="body2" color="text.secondary">
              Completa la información
            </Typography>
          </SubtitleIconBox>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <StyledTextField
                fullWidth
                label="Nombre"
                variant="outlined"
                value={formData.firstName}
                onChange={(e) => handleChange("firstName", e.target.value)}
              />
            </Grid>
            <Grid item xs={6}>
              <StyledTextField
                fullWidth
                label="Apellidos"
                variant="outlined"
                value={formData.lastName}
                onChange={(e) => handleChange("lastName", e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <StyledTextField
                fullWidth
                label="Empresa (opcional)"
                variant="outlined"
                value={formData.company}
                onChange={(e) => handleChange("company", e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <StyledTextField
                fullWidth
                label="Dirección"
                variant="outlined"
                value={formData.address}
                onChange={(e) => handleChange("address", e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <StyledTextField
                fullWidth
                label="Apartamento, suite, etc. (opcional)"
                variant="outlined"
                value={formData.apt}
                onChange={(e) => handleChange("apt", e.target.value)}
              />
            </Grid>
            <Grid item xs={4}>
              <StyledTextField
                fullWidth
                label="Ciudad"
                variant="outlined"
                value={formData.city}
                onChange={(e) => handleChange("city", e.target.value)}
              />
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth>
                <InputLabel>País/Región</InputLabel>
                <Select
                  value={formData.country}
                  label="País/Región"
                  onChange={(e) => handleChange("country", e.target.value)}
                  sx={{ borderRadius: 2, backgroundColor: "white" }}
                >
                  <MenuItem value="México">México</MenuItem>
                  <MenuItem value="Estados Unidos">Estados Unidos</MenuItem>
                  <MenuItem value="España">España</MenuItem>
                  <MenuItem value="Argentina">Argentina</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <StyledTextField
                fullWidth
                label="Estado"
                variant="outlined"
                value={formData.state}
                onChange={(e) => handleChange("state", e.target.value)}
              />
            </Grid>
            <Grid item xs={6}>
              <StyledTextField
                fullWidth
                label="Código Postal"
                variant="outlined"
                value={formData.zip}
                onChange={(e) => handleChange("zip", e.target.value)}
              />
            </Grid>
            <Grid item xs={6}>
              <StyledTextField
                fullWidth
                label="Teléfono (opcional)"
                variant="outlined"
                value={formData.phone}
                onChange={(e) => handleChange("phone", e.target.value)}
              />
            </Grid>
          </Grid>
        </SectionBox>

        {/* Método de pago */}
        <SectionBox>
          <HeaderText gutterBottom color="primary.dark">
            Método de Pago
          </HeaderText>
          <SubtitleIconBox>
            <Payment color="primary" />
            <Typography variant="body2" color="text.secondary">
              Selecciona el método de pago
            </Typography>
          </SubtitleIconBox>
          <FormControl component="fieldset" sx={{ mb: 2 }}>
            <RadioGroup
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
            >
              <FormControlLabel
                value="card"
                control={<Radio color="primary" />}
                label={
                  <Typography variant="body1">
                    Tarjeta de Crédito / Débito
                  </Typography>
                }
              />
            </RadioGroup>
          </FormControl>

          <Collapse in={paymentMethod === "card"}>
            <animated.div style={springProps}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  mt: 2,
                  backgroundColor: "#ffffffbf",
                  boxShadow: 1,
                }}
              >
                <SubtitleIconBox>
                  <CreditCard color="primary" />
                  <Typography variant="body2" color="text.secondary">
                    Información de la tarjeta
                  </Typography>
                </SubtitleIconBox>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <StyledTextField
                      fullWidth
                      label="Número de Tarjeta"
                      variant="outlined"
                      value={formData.cardNumber}
                      onChange={(e) =>
                        handleChange("cardNumber", e.target.value)
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <StyledTextField
                      fullWidth
                      label="Nombre en la Tarjeta"
                      variant="outlined"
                      value={formData.cardName}
                      onChange={(e) => handleChange("cardName", e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <StyledTextField
                      fullWidth
                      label="Expiración (MM/AA)"
                      variant="outlined"
                      value={formData.cardExp}
                      onChange={(e) => handleChange("cardExp", e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <StyledTextField
                      fullWidth
                      label="CVC"
                      variant="outlined"
                      value={formData.cardCvc}
                      onChange={(e) => handleChange("cardCvc", e.target.value)}
                    />
                  </Grid>
                </Grid>
              </Box>
            </animated.div>
          </Collapse>

          {/* Botón Pagar */}
          <Box mt={4} display="flex" justifyContent="flex-end">
            <StyledButton
              variant="contained"
              color="primary"
              size="large"
              onClick={handleSubmit}
              startIcon={<CheckCircle />}
              disabled={paymentMethod !== "card"}
            >
              Proceder al pago
            </StyledButton>
          </Box>
        </SectionBox>
      </FormContainer>

      {/* Columna derecha: resumen */}
      <SummaryContainer>
        <Typography
          variant="h6"
          gutterBottom
          fontWeight="bold"
          color="primary.dark"
        >
          Resumen del Pedido
        </Typography>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            mb: 1,
            mt: 2,
          }}
        >
          <Typography variant="body2" color="text.primary">
            Mensualidad fija.
          </Typography>
          <Typography variant="body2" color="text.primary">
            ${defaultPlan.precio}.00 pesos
          </Typography>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
          <Typography variant="body2" color="text.primary">
            Paquete de usuarios seleccionados
          </Typography>
          <Typography variant="body2" color="text.primary">
            ${selectedPlanPrice}.00 pesos
          </Typography>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
          <Typography variant="body2" color="text.primary">
            8% IVA
          </Typography>
          <Typography variant="body2" color="text.primary">
            ${IVA.toFixed(2)} pesos
          </Typography>
        </Box>
        <Divider sx={{ my: 1 }} />
        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
          <Typography
            variant="subtitle1"
            fontWeight="bold"
            color="text.primary"
          >
            Total
          </Typography>
          <Typography
            variant="subtitle1"
            fontWeight="bold"
            color="text.primary"
          >
            ${total.toFixed(2)} pesos
          </Typography>
        </Box>
      </SummaryContainer>
    </CheckoutContainer>
  );
};

export default Checkout;
